import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCompanyLocationKey,
  ShopifyCustomerKey,
  ShopifyShopKey,
  ShopifyStoreCreditAccountKey,
  ShopifyStoreCreditAccountToCompanyLocationKey,
  ShopifyStoreCreditAccountToCustomerKey,
  ShopifyStoreCreditAccountToCustomerOwnerKey,
  ShopifyStoreCreditAccountToShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import { buildConnectionSpec, ConnectionModelSpecVersion } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyStoreCreditAccountKey,
  "2024-07": {
    name: "Shopify Store Credit Account",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Balance",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "balance",
            graphQLSelection: `
            amount
            currencyCode
            `,
          },
        },
      },
      //TODO
      // {
      //   name: "Transactions",
      //   fieldType: FieldType.HasMany,
      //   configuration: {
      //     dependent: "delete",
      //     relatedModelKey: ShopifyStoreCreditAccountTransactionKey,
      //     inverseFieldKey: ShopifyStoreCreditAccountTransactionToStoreCreditAccountKey,
      //   },
      // },
      {
        name: "Owner",
        fieldType: FieldType.BelongsTo,
        key: ShopifyStoreCreditAccountToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "owner",
            graphQLSelection: `
              ... on Customer {
              id
            }
          `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyStoreCreditAccountToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      // With Customer REST sync
      parent: "customer",
      resource: "customer.storeCreditAccounts",
    },
  },
  "2025-04": {
    syncConfig: {
      type: "graphql_sublist",
      // With Customer GraphQL sync
      parent: "customers",
      resource: "customer.storeCreditAccounts",
    },
  },
  "2025-10": {
    fields: [
      {
        name: "Owner",
        fieldType: FieldType.BelongsTo,
        key: ShopifyStoreCreditAccountToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "owner",
            graphQLSelection: `
              ... on Customer {
              id
            }
          `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
        isDeleted: true,
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyStoreCreditAccountToCustomerOwnerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customerOwner",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Company Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyStoreCreditAccountToCompanyLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "companyLocationOwner",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyLocationKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: ["customers", "companyLocations"],
      resource: ["customer.storeCreditAccounts", "companyLocation.storeCreditAccounts"],
      additionalSelection: `
      owner {
        ... on Customer {
          id
        }
        ... on CompanyLocation {
          id
        }
      }
      `,
    },
  },
};

export const ShopifyStoreCreditAccount = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
